// HTTP client for @flight/web. Copied from packages/admin/src/lib/api.ts.
// Changes from admin version:
//   • Storage keys are 'flight_web_access' / 'flight_web_refresh' (no collision with admin)
//   • deviceId sent as 'web' (not 'admin-web')
//   • Base URL points to same /api/v1 — proxied to :3000 in dev via vite.config.ts

import axios, { type AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL =
  (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? '/api/v1';

export const ACCESS_KEY = 'flight_web_access';
export const REFRESH_KEY = 'flight_web_refresh';

export function getStoredAccessToken(): string | null {
  return sessionStorage.getItem(ACCESS_KEY);
}

export function getStoredRefreshToken(): string | null {
  return sessionStorage.getItem(REFRESH_KEY);
}

export function setStoredTokens(access: string, refresh?: string): void {
  sessionStorage.setItem(ACCESS_KEY, access);
  if (refresh) sessionStorage.setItem(REFRESH_KEY, refresh);
}

export function clearStoredTokens(): void {
  sessionStorage.removeItem(ACCESS_KEY);
  sessionStorage.removeItem(REFRESH_KEY);
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getStoredAccessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }

  // Auto-inject Idempotency-Key on mutating calls.
  const method = (config.method ?? 'get').toLowerCase();
  if ((method === 'post' || method === 'patch') && !config.headers.has('Idempotency-Key')) {
    config.headers.set('Idempotency-Key', crypto.randomUUID());
  }

  return config;
});

interface RetryableConfig extends AxiosRequestConfig {
  _retried?: boolean;
}

let refreshInFlight: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  const refresh = getStoredRefreshToken();
  if (!refresh) return null;
  try {
    const res = await axios.post(
      `${API_BASE_URL}/auth/refresh`,
      { refreshToken: refresh, deviceId: 'web' },
      {
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': crypto.randomUUID(),
        },
      },
    );
    const data = res.data as { data?: { accessToken?: string; refreshToken?: string } };
    const newAccess = data.data?.accessToken;
    const newRefresh = data.data?.refreshToken;
    if (!newAccess) return null;
    setStoredTokens(newAccess, newRefresh);
    return newAccess;
  } catch {
    return null;
  }
}

api.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const original = (err.config ?? {}) as RetryableConfig;

    const status = err.response?.status;
    const url = original.url ?? '';
    if (
      status !== 401 ||
      original._retried ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/login')
    ) {
      return Promise.reject(err);
    }

    refreshInFlight ??= performRefresh().finally(() => {
      refreshInFlight = null;
    });
    const newAccess = await refreshInFlight;

    if (!newAccess) {
      clearStoredTokens();
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
      return Promise.reject(err);
    }

    original._retried = true;
    original.headers = { ...(original.headers ?? {}), Authorization: `Bearer ${newAccess}` };
    return api.request(original);
  },
);

export interface ApiErrorShape {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export function extractApiError(err: unknown): ApiErrorShape {
  const e = err as AxiosError<{ error?: ApiErrorShape; message?: string }>;
  const body = e.response?.data;
  if (body?.error?.code) {
    return body.error;
  }
  return {
    code: 'NETWORK_ERROR',
    message: (err as AxiosError).message || 'Network error',
  };
}
